import { useState, useRef } from 'react';
import { Printer } from 'lucide-react';
import { useBusiness } from '../../../contexts/BusinessContext';
import { usePrinters } from '../../../hooks/usePrinters';
import { ticketRenderer } from '../../../services/ticketRenderer';

// Variable global para controlar si hay una impresión en curso
let isPrintingGlobally = false;
let printTimeout = null;

/**
 * Botón para imprimir diferentes tipos de tickets
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.sale - Datos de la venta
 * @param {string} props.type - Tipo de ticket (delivery, fulfillment, backup)
 * @param {string} props.label - Etiqueta del botón
 * @param {string} props.className - Clases CSS adicionales
 * @param {string} props.id - ID del botón
 */
export function PrintTicketButton({ sale, type = 'delivery', label = 'Imprimir', className = '', id = '' }) {
  const { business } = useBusiness();
  const { updatePrinterStatus } = usePrinters();
  const [isLoading, setIsLoading] = useState(false);
  const printRequestRef = useRef(null);

  const handlePrint = async () => {
    if (!business?.id || !sale) {
      alert('No hay información suficiente para imprimir');
      return;
    }

    // Evitar múltiples impresiones simultáneas
    if (isPrintingGlobally) {
      console.log('Ya hay una impresión en curso, ignorando solicitud');
      return;
    }

    // Generar un ID único para esta solicitud de impresión
    const requestId = Date.now().toString();
    printRequestRef.current = requestId;

    setIsLoading(true);
    isPrintingGlobally = true;

    // Limpiar cualquier timeout anterior
    if (printTimeout) {
      clearTimeout(printTimeout);
    }

    try {
      console.log(`Generando ticket de ${type} para la venta #${sale.display_id || sale.id}`);
      
      // Preparar los datos para el ticket
      const customer = sale.customer_name ? { name: sale.customer_name } : null;
      
      // Generar el ticket utilizando el sistema de templates
      const ticketContent = ticketRenderer.generateTicket(
        {
          business,
          sale,
          items: sale.items,
          customer
        },
        type,
        'plain' // Usamos texto plano para compatibilidad con el printer client actual
      );
      
      // Mapear los tipos de ticket de nuestra aplicación a los tipos del cliente de impresión
      const printerClientType = {
        'delivery': 'delivery',
        'fulfillment': 'preparation',
        'backup': 'copy'
      }[type] || 'delivery';
      
      console.log('Enviando ticket al servicio de impresión');
      const response = await fetch('https://tibo-printer-service.fly.dev/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          printer_id: "3", // ID fijo que usa el printer client
          business_id: business?.id,
          content: {
            text: ticketContent,
            format: {
              alignment: 'left',
              fontSize: 1
            },
            // Agregar información específica para el cliente de impresión
            print_type: printerClientType,
            single_ticket: true, // Indicar que solo queremos imprimir un tipo de ticket
            orderNumber: sale.display_id || sale.id
          },
          type: type
        })
      });
      
      // Verificar si esta solicitud sigue siendo la actual
      if (printRequestRef.current !== requestId) {
        console.log('Solicitud de impresión cancelada por una más reciente');
        return;
      }
      
      const data = await response.json();
      console.log('Respuesta de impresión:', data);
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Actualizar el estado de la impresora
      await updatePrinterStatus("3", true);
      alert(`Ticket de ${type} enviado a la impresora`);
    } catch (error) {
      console.error(`Error al imprimir ticket de ${type}:`, error);
      await updatePrinterStatus("3", false);
      alert(`Error al imprimir: ${error.message}`);
    } finally {
      setIsLoading(false);
      
      // Establecer un timeout para permitir nuevas impresiones después de un tiempo
      printTimeout = setTimeout(() => {
        isPrintingGlobally = false;
      }, 3000); // 3 segundos de espera entre impresiones
    }
  };

  // Determinar el texto del botón según el tipo
  let buttonText = label;
  if (type === 'delivery') buttonText = label || 'Imprimir ticket';
  if (type === 'fulfillment') buttonText = label || 'Imprimir preparación';
  if (type === 'backup') buttonText = label || 'Imprimir respaldo';

  return (
    <button
      id={id}
      onClick={handlePrint}
      disabled={isLoading || !business?.id || !sale || isPrintingGlobally}
      className={`flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${className}`}
    >
      <Printer className="w-4 h-4" />
      {isLoading ? 'Imprimiendo...' : isPrintingGlobally ? 'Espere...' : buttonText}
    </button>
  );
} 